// @ts-nocheck
import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, Lock, Eye, Database, FileText, Mail } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';

const Privacy: React.FC = () => {
  const { isDark } = useTheme();

  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: [
        {
          subtitle: 'Account Information',
          text: 'When you create an account, we collect your email address and authentication credentials. This information is necessary to provide you access to our services and communicate with you.'
        },
        {
          subtitle: 'Usage Data',
          text: 'We collect information about how you use our platform, including the cryptocurrencies you analyze, features you access, and your interaction patterns. This helps us improve our services and provide better recommendations.'
        },
        {
          subtitle: 'Technical Data',
          text: 'We automatically collect certain technical information including your IP address, browser type, device information, and operating system to ensure platform security and optimal performance.'
        }
      ]
    },
    {
      icon: Lock,
      title: 'How We Use Your Information',
      content: [
        {
          subtitle: 'Service Provision',
          text: 'We use your information to provide, maintain, and improve our AI-powered cryptocurrency analysis services, including generating personalized reports and recommendations.'
        },
        {
          subtitle: 'Communication',
          text: 'We may send you service-related notifications, updates about new features, and important security alerts. You can control your communication preferences in your account settings.'
        },
        {
          subtitle: 'Analytics and Improvements',
          text: 'We analyze usage patterns to understand how our platform is used, identify areas for improvement, and develop new features that benefit our community.'
        }
      ]
    },
    {
      icon: Shield,
      title: 'Data Security',
      content: [
        {
          subtitle: 'Encryption',
          text: 'All data transmitted between your device and our servers is encrypted using industry-standard SSL/TLS protocols. Sensitive information is encrypted at rest using advanced encryption standards.'
        },
        {
          subtitle: 'Access Controls',
          text: 'We implement strict access controls to ensure that only authorized personnel can access user data, and only when necessary for providing support or maintaining the platform.'
        },
        {
          subtitle: 'Security Monitoring',
          text: 'We continuously monitor our systems for potential security threats and vulnerabilities, implementing updates and patches promptly to protect your data.'
        }
      ]
    },
    {
      icon: Eye,
      title: 'Data Sharing and Disclosure',
      content: [
        {
          subtitle: 'Third-Party Services',
          text: 'We use trusted third-party services for specific functions such as authentication, data storage, and analytics. These providers are contractually obligated to protect your data and use it only for the purposes we specify.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose your information if required by law, such as in response to a valid court order, subpoena, or other legal process, or to protect our rights and the safety of our users.'
        },
        {
          subtitle: 'No Sale of Data',
          text: 'We do not sell, rent, or trade your personal information to third parties for marketing purposes. Your data is yours, and we respect that.'
        }
      ]
    },
    {
      icon: FileText,
      title: 'Your Rights and Choices',
      content: [
        {
          subtitle: 'Access and Portability',
          text: 'You have the right to access your personal data and request a copy of the information we hold about you in a portable format.'
        },
        {
          subtitle: 'Correction and Deletion',
          text: 'You can update your account information at any time. You also have the right to request deletion of your account and associated data, subject to our legal retention obligations.'
        },
        {
          subtitle: 'Opt-Out',
          text: 'You can opt out of non-essential communications and control how we use your data for analytics purposes through your account settings.'
        }
      ]
    },
    {
      icon: Database,
      title: 'Data Retention',
      content: [
        {
          subtitle: 'Active Accounts',
          text: 'We retain your account information and usage data as long as your account is active and for a reasonable period thereafter to comply with legal obligations and resolve disputes.'
        },
        {
          subtitle: 'Deleted Accounts',
          text: 'When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal, regulatory, or security purposes.'
        }
      ]
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'}`}>
      {/* Header */}
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
            isDark
              ? 'bg-emerald-500/20 border border-emerald-500/30'
              : 'bg-emerald-50 border border-emerald-200'
          }`}>
            <Shield className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={`text-sm font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
              Privacy Policy
            </span>
          </div>

          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Your Privacy Matters
          </h1>

          <p className={`text-lg ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <div className={`p-6 rounded-2xl border mb-8 ${
          isDark
            ? 'bg-white/5 border-white/10'
            : 'bg-white border-gray-200'
        }`}>
          <p className={`text-base leading-relaxed ${
            isDark ? 'text-zinc-300' : 'text-gray-700'
          }`}>
            At InvestVerse.AI, we take your privacy seriously. This Privacy Policy explains how we collect, use, protect,
            and share your personal information when you use our AI-powered cryptocurrency analysis platform. By using our
            services, you agree to the practices described in this policy.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;

            return (
              <div
                key={index}
                className={`p-8 rounded-2xl border ${
                  isDark
                    ? 'bg-white/5 border-white/10'
                    : 'bg-white border-gray-200'
                }`}
              >
                {/* Section Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                    isDark ? 'bg-blue-500/10' : 'bg-blue-50'
                  }`}>
                    <Icon className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <h2 className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {section.title}
                  </h2>
                </div>

                {/* Section Content */}
                <div className="space-y-6 ml-16">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      <h3 className={`text-lg font-semibold mb-2 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.subtitle}
                      </h3>
                      <p className={`text-base leading-relaxed ${
                        isDark ? 'text-zinc-400' : 'text-gray-600'
                      }`}>
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Cookies Notice */}
        <div className={`mt-8 p-6 rounded-2xl border ${
          isDark
            ? 'bg-orange-500/10 border-orange-500/20'
            : 'bg-orange-50 border-orange-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Cookies and Tracking Technologies
          </h3>
          <p className={`text-base leading-relaxed ${
            isDark ? 'text-zinc-300' : 'text-gray-700'
          }`}>
            We use cookies and similar tracking technologies to enhance your experience, remember your preferences,
            and analyze platform usage. You can control cookie settings through your browser, though disabling certain
            cookies may affect platform functionality.
          </p>
        </div>

        {/* Contact Section */}
        <div className={`mt-8 p-8 rounded-2xl border text-center ${
          isDark
            ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/20'
            : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
        }`}>
          <Mail className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <h3 className={`text-xl font-bold mb-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Questions About Privacy?
          </h3>
          <p className={`text-base mb-4 ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us.
          </p>
          <a
            href="mailto:privacy@investverse.ai"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
              isDark
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Mail className="w-4 h-4" />
            Contact Privacy Team
          </a>
        </div>

        {/* Updates Notice */}
        <div className={`mt-8 p-4 rounded-xl border text-sm ${
          isDark
            ? 'bg-white/5 border-white/10 text-zinc-400'
            : 'bg-gray-50 border-gray-200 text-gray-600'
        }`}>
          <p>
            <strong className={isDark ? 'text-white' : 'text-gray-900'}>Updates to This Policy:</strong> We may update
            this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify
            you of any material changes by posting the updated policy on this page and updating the "Last updated" date.
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Privacy;
